
import React, { useState, useEffect } from "react";
import {
  Box, Typography, Card, CardContent, Button, Container,
  IconButton, CircularProgress, Chip, Paper, Grid,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  ToggleButton, ToggleButtonGroup, Divider
} from "@mui/material";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import ViewListIcon from "@mui/icons-material/ViewList";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import RefreshIcon from "@mui/icons-material/Refresh";
import { useNavigate } from "react-router-dom";
import { fetchUserFromRedis } from "../HomePage/AuthonticationScreens/Register";
import Footer from "../../Components/Common/Footer";
import { apiRequest } from "../../fetchSetup";
import ClearIcon from "@mui/icons-material/Clear";
import  bg2 from "../Logos/med_background2.png"



const STATUS_COLORS = {
  bill_generated: { bg: "#DBEAFE", text: "#2563EB", label: "Bill Generated" },
  confirmed: { bg: "#DBEAFE", text: "#2563EB", label: "Confirmed" },
  pending: { bg: "#FEF3C7", text: "#D97706", label: "Pending" },
  out_for_delivery: { bg: "#FEF3C7", text: "#D97706", label: "Out for delivery" },
  dispatched: { bg: "#E0E7FF", text: "#6366F1", label: "Dispatched" },
  delivered: { bg: "#D1FAE5", text: "#059669", label: "Delivered" },
  cancelled: { bg: "#FEE2E2", text: "#DC2626", label: "Cancelled" },
  returned: {
    bg: '#FEE2E2',
    text: '#DC2626',
    label: 'Returned'
  },

  partially_returned: {
    bg: '#FEF3C7',
    text: '#D97706',
    label: 'Partially Returned'
  },
};

const MyOrdersScreen = () => {
  const navigate = useNavigate();

  const [user, setUser] = useState(null);
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState("all");
  const [viewMode, setViewMode] = useState("table");

  const filters = [
    { id: "all", label: "All" },
    { id: "bill_generated", label: "Bill Ready" },
    { id: "confirmed", label: "Confirmed" },
    { id: "awaiting_bill", label: "Pending" },
    { id: "delivered", label: "Delivered" },
    { id: "out_for_delivery", label: "Out for delivery" },
    { id: "cancelled", label: "Cancelled" },
  ];

  useEffect(() => {
    loadUserData();
  }, []);

  useEffect(() => {
    if (user?.email) fetchOrders(user.email);
  }, [user]);

  useEffect(() => {
    filterOrders();
  }, [selectedFilter, orders]);

  const loadUserData = async () => {
    const userId = sessionStorage.getItem("userId");
    const token = sessionStorage.getItem("token");

    if (!userId || !token) return;

    try {
      const userData = await fetchUserFromRedis(userId, token);
      setUser(userData || null);
    } catch {
      setUser(null);
    }
  };

  const fetchOrders = async (email) => {
    try {
      setLoading(true);
      const res = await apiRequest(
        `${process.env.REACT_APP_GET_USER_MEDICINE_ORDERS}/${email}`
      );
      const data = await res.json();
      setOrders(data.orders || []);
      

    } catch (err) {
      console.error(err);
      setOrders([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const onRefresh = () => {
    setRefreshing(true);
    if (user?.email) fetchOrders(user.email);
  };

  const filterOrders = () => {
    if (selectedFilter === "all") {
      setFilteredOrders(orders);
    } else {
      setFilteredOrders(
        orders.filter((o) => o.order_status === selectedFilter)
      );
    }
  };

  const formatDate = (date) => {
    if (!date) return "N/A";
    return new Date(date).toLocaleDateString("en-IN", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  const getStatus = (status) =>
    STATUS_COLORS[status] || STATUS_COLORS.pending;

  const handleOrderClick = (order) => {
    navigate("/order-details", { state: { orderId: order.order_id } });
  };

  // ================= CARD VIEW =================
  const renderOrderCard = (order) => {
    const status = getStatus(order.order_status);

    return (
      <Card
        key={order.order_id}
        elevation={3}
         sx={{
        borderRadius: 3,
        width: 700,       
        flexGrow: 1,        
        display: "flex",
        flexDirection: "column",
        height: "auto",        
        transition: "all 0.3s",
        "&:hover": { 
          transform: "translateY(-4px)",
          boxShadow: "0 12px 24px rgba(0,109,119,0.15)"
        },
        border: "1px solid #E5E7EB"
      }}
      >
        <CardContent
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          justifyContent: "space-between",
        }}>
          {/* Header */}
          <Box display="flex" justifyContent="space-between">
            <Typography fontWeight={700}>
              #{order.order_id}
            </Typography>

            <Chip
              label={status.label}
              sx={{
                bgcolor: status.bg,
                color: status.text,
                fontWeight: 600,
                pointerEvents: "none",
              }}
            />
          </Box>

          <Typography fontSize={12} color="text.secondary">
            {formatDate(order.created_at)}
          </Typography>
          <Typography fontSize={12} color="text.secondary">
            {order.order_type}
          </Typography>

          <Divider sx={{ my: 1.5 }} />

          {/* Items */}
          {order.items?.slice(0, 2).map((item, i) => (
            <Typography key={i} fontSize={13}>
              • {item.medicine_name} x {item.quantity}
            </Typography>
          ))}

          {order.items?.length > 2 && (
            <Typography fontSize={12} color="text.secondary">
              +{order.items.length - 2} more items
            </Typography>
          )}

          <Divider sx={{ my: 1.5 }} />

          {/* Footer */}
          <Box display="flex" justifyContent="space-between">
            <Typography fontWeight={700} color="#006D77">
              ₹{order.total_amount}
            </Typography>

            <Button
              size="small"
              variant="outlined"
              onClick={() => handleOrderClick(order)}
              sx={{ textTransform: "none",bgcolor:  "#006D77",color:"white" }}
            >
              View
            </Button>
          </Box>
        </CardContent>
      </Card>
    );
  };

  // ================= TABLE VIEW =================
  const renderTable = () => (
    <TableContainer component={Paper} elevation={3} sx={{ borderRadius: 3 }}>
      <Table>
        <TableHead>
          <TableRow sx={{ bgcolor: "#006D77" }}>
            <TableCell sx={{ color: "white", fontWeight: 700, fontSize: 14 }}>Order</TableCell>
            <TableCell sx={{ color: "white", fontWeight: 700, fontSize: 14 }}>Medicine Name</TableCell>
            <TableCell sx={{ color: "white", fontWeight: 700, fontSize: 14 }}>Order Type</TableCell>
            <TableCell sx={{ color: "white", fontWeight: 700, fontSize: 14 }}>Date</TableCell>
            <TableCell sx={{ color: "white", fontWeight: 700, fontSize: 14 }}>Status</TableCell>
            <TableCell sx={{ color: "white", fontWeight: 700, fontSize: 14 }}>Total</TableCell>
            <TableCell sx={{ color: "white", fontWeight: 700, fontSize: 14 }}>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {filteredOrders.map((o) => {
            const status = getStatus(o.order_status);
            return (
              <TableRow key={o.order_id}>
                <TableCell  sx={{ fontWeight: 600 }}>#{o.order_id}</TableCell>
                <TableCell  sx={{ fontWeight: 600 }}>
                 {/* Items */}
                    {o.items?.length > 0 ? (
            o.items.slice(0, 2).map((item, i) => (
              <Typography key={i} fontSize={13} fontWeight={600}>
                {item.medicine_name} x {item.quantity}
              </Typography>
            ))
          ) : (
            <Typography fontSize={13} fontWeight={600}>
              -
            </Typography>
          )}

          {o.items?.length > 2 && (
            <Typography fontSize={12} color="text.secondary">
              +{o.items.length - 2} more items
            </Typography>
          )}
          </TableCell>
                <TableCell  sx={{ fontWeight: 600 }}>{o.order_type}</TableCell>
                <TableCell  sx={{ fontWeight: 600 }}>{formatDate(o.created_at)}</TableCell>
                <TableCell>
                  <Chip
                    label={status.label}
                    size="small"
                    sx={{
                      bgcolor: status.bg,
                      color: status.text,
                    }}
                  />
                </TableCell>
                <TableCell  sx={{ fontWeight: 600, color:"#006D77" }}>₹{o.total_amount}</TableCell>
                <TableCell>
                  <Button
                    size="small"
                    sx={{bgcolor:"#006D77",color:"white"}}
                    onClick={() => handleOrderClick(o)}
                  >
                    View
                  </Button>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );

  if (loading) {
    return (
      <Box textAlign="center" mt={10}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <Box sx={{ minHeight: "100vh", bgcolor: "#F9FAFB"}}>
        
        {/* HEADER */}
        <Paper sx={{ mb: 2, backgroundImage: `
                            linear-gradient(90deg, rgba(0,109,119,0.9) 0%, rgba(0, 169, 181, 0.61) 100%),
                            url(${bg2})
                          `,
                          backgroundSize: "cover",
                          backgroundPosition: "center",
                          backgroundRepeat: "no-repeat" }}>
          <Container>
            <Box display="flex" alignItems="center" py={2} sx={{color:"#ffffff"}}>
              <IconButton onClick={() => window.history.back()}  sx={{
                "&:hover": { bgcolor: "#F3F4F6", color:"#000000" },
                color:"#ffffff"
              }}>
                <ArrowBackIcon />
              </IconButton>

  <Typography
              variant="h4"
              sx={{
                color: "#ffffff",
                fontWeight: 600,
                fontSize: { xs: "1rem", sm: "1.4rem", md: "2rem" },
                textAlign: "center",
                flex: 1,
              }}
            >
              My Medicine Orders
            </Typography>

                {/* VIEW TOGGLE */}
         
            </Box>
          </Container>
        </Paper>

        {/* FILTERS */}
        <Container maxWidth="xl" sx={{ py: 3 }}>
          {/* Header Row */}
  <Box
    display="flex"
    justifyContent="left"
    alignItems="center"
  >
    <Typography
      sx={{
        fontSize: 18,
        fontWeight: 700,
        color: "#111827",
      }}
    >
      Filters
    </Typography>

  </Box>

  {/* Chips */}
  <Box
    display="flex"
    gap={2} // 🔥 increased spacing between chips
    overflow="auto"
    sx={{
      "&::-webkit-scrollbar": { display: "none" },
    }}
    mb={3}
    py={1}
  >
      {filters.map((f) => {
    const count =
      f.id === "all"
        ? orders.length
        : orders.filter((o) => o.order_status === f.id).length;

    return (
      <Chip
        key={f.id}
                label={
          <Box display="flex" alignItems="center" gap={1}>
            <span>{f.label}</span>

            {/* 🔢 Count badge */}
            <Box
              sx={{
                bgcolor: selectedFilter === f.id ? "#fff" : "#006D77",
                color: selectedFilter === f.id ? "#006D77" : "#fff",
                fontSize: 15,
                fontWeight: 700,
                px: 0.8,
                borderRadius: "10px",
                minWidth: 20,
                textAlign: "center",
              }}
            >
              {count}
            </Box>
          </Box>
        }
        onClick={() => setSelectedFilter(f.id)}
        sx={{
          px: 1.5,
          py: 1,
          height:35,
          fontWeight: 600,
          bgcolor: selectedFilter === f.id ? "#006D77" : "#fff",
          color: selectedFilter === f.id ? "#fff" : "#000",
          border: "1.5px solid #006D77", // ✅ proper border
          borderRadius: "20px",
          cursor: "pointer",
          "&:hover": {
            bgcolor:
              selectedFilter === f.id ? "#005A63" : "#E5E7EB",
          },
        }}
      />
      
    )})}
    {selectedFilter !== "all" && (
      <Button
        size="small"
        onClick={() => setSelectedFilter("all")}
        startIcon={<ClearIcon />} // ✅ clear icon added
        sx={{
          textTransform: "none",
          fontWeight: 600,
          color: "#006D77",
        }}
      >
        Clear Filter
      </Button>
    )}
     <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={(e, val) => val && setViewMode(val)}
            sx={{ml:7}}
          >
            <ToggleButton value="card" >
              <ViewModuleIcon />
              Card
            </ToggleButton>
            <ToggleButton value="table">
              <ViewListIcon />
              Table
            </ToggleButton>
          </ToggleButtonGroup>
              <IconButton onClick={onRefresh}>
                <RefreshIcon />
              </IconButton>
  </Box>

        

          {/* CONTENT */}
          {filteredOrders.length === 0 ? (
            <Typography textAlign="center">No Orders</Typography>
          ) : viewMode === "card" ? (
            <Grid container spacing={2}>
              {filteredOrders.map((o) => (
                <Grid item xs={12} sm={6} md={6} key={o.order_id}>
                  {renderOrderCard(o)}
                </Grid>
              ))}
            </Grid>
          ) : (
            renderTable()
          )}
        </Container>
      </Box>

      <Footer />
    </>
  );
};

export default MyOrdersScreen;